import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DateTime from '@ricoh/ria.client.core/plugins/moment';
import { isNotNull } from '@ricoh/ria.client.core/utils/data/object';
import { z } from 'zod';

import fieldLogic from '~/common/logic/feature/settings/batch-type-library/field';

import type { FieldDoubleBlindKeyingSchemaCreator } from './_double-blind-keying.type';

import { emptySchema } from './_empty';

/**
 * Use Field DoubleBlindKeying Schema Creator.
 *
 * @returns {FieldDoubleBlindKeyingSchemaCreator} Field DoubleBlindKeying Schema Creator.
 */
export function useFieldDoubleBlindKeyingSchemaCreator(): FieldDoubleBlindKeyingSchemaCreator {
  const { t } = useTranslation();

  return useCallback<FieldDoubleBlindKeyingSchemaCreator>(
    ({ fieldName, type, conditions }) => {
      const messages = {
        invalid_type_error: t('validation:invalidType', { fieldName }),
        invalid_literal: (value: unknown) => t('validation:match', { value }),
      };

      const equalOptionalCondition = conditions.find(({ operator }) => operator === 'shouldEquals');
      if (!equalOptionalCondition) {
        return undefined;
      }

      const errorMap: z.ZodErrorMap = (issue) => {
        if (issue.code === 'invalid_literal') {
          let value;
          switch (equalOptionalCondition.value?.type) {
            case 'list.date':
            case 'date': {
              value = DateTime.nullablePattern({ date: typeof issue.expected === 'string' ? issue.expected : undefined })?.format(
                DateTime.config.format.display.date,
              );

              break;
            }

            case 'list.time':
            case 'time': {
              value = DateTime.nullablePattern({ time: typeof issue.expected === 'string' ? issue.expected : undefined })?.format(
                DateTime.config.format.display.time,
              );

              break;
            }

            case 'list.datetime':
            case 'datetime': {
              value = DateTime.nullable(typeof issue.expected === 'string' ? issue.expected : undefined, true)?.format(DateTime.config.format.display.datetime);

              break;
            }

            case 'list.boolean':
            case 'boolean': {
              value = typeof issue.expected === 'boolean' ? t(issue.expected.toString()) : undefined;

              break;
            }

            default: {
              value = equalOptionalCondition.value?.value;

              break;
            }
          }

          return { message: messages.invalid_literal(value) };
        }

        return { message: messages.invalid_type_error };
      };

      if (fieldLogic.isDataType.table(type)) {
        return z.any();
      }

      let value;
      if (fieldLogic.isDataType.list(type)) {
        value = Array.isArray(equalOptionalCondition.value?.value) ? (equalOptionalCondition.value.value.at(0) as z.Primitive) : undefined;
      } else {
        value = equalOptionalCondition.value?.value as z.Primitive;
      }

      if (isNotNull(value)) {
        return z.literal(value, { errorMap });
      }

      return emptySchema.all(errorMap, messages.invalid_literal(''));
    },
    [t],
  );
}
